import { computed } from 'vue';
import { useStore } from 'vuex';
import { IUserInfo } from '@/helpers/interfaces';

export interface ISiteNavLink {
  text: string;
  url: string;
  variant?: 'default' | 'auth';
}

export interface ISiteCtaLink {
  text: string;
  url: string;
}

export interface ISiteNavigation {
  navLinks: ISiteNavLink[];
  ctaLink: ISiteCtaLink | null;
}

export function buildSiteNavigation(
  user: IUserInfo | null,
  hasActiveEvent: boolean,
): ISiteNavigation {
  if (hasActiveEvent) {
    return buildSubscribedNavigation(user);
  }
  return buildBaseNavigation(user);
}

function buildSubscribedNavigation(user: IUserInfo | null): ISiteNavigation {
  const navLinks: ISiteNavLink[] = [
    { text: 'האירוע', url: '/event' },
    { text: 'פרופיל', url: '/profile' },
    { text: 'צור קשר', url: '/contact-us' },
  ];

  if (user) {
    navLinks.unshift({ text: 'התנתק', url: '/logout', variant: 'auth' });
  }

  return { navLinks, ctaLink: null };
}

function buildBaseNavigation(user: IUserInfo | null): ISiteNavigation {
  const navLinks: ISiteNavLink[] = [
    { text: 'איך זה עובד', url: '/#how-it-works' },
    { text: 'פרטיות ואבטחה', url: '/#privacy-by-design' },
    { text: 'איך זה נראה', url: '/#how-it-looks' },
    { text: 'למי זה מתאים', url: '/#who-is-it-for' },
    { text: 'המלצות', url: '/#testimonials' },
    { text: 'המסלולים', url: '/#pricing' },
    { text: 'צור קשר', url: '/contact-us' },
  ];

  if (user) {
    navLinks.unshift({ text: 'התנתק', url: '/logout', variant: 'auth' });
    navLinks.push({ text: 'פרופיל', url: '/profile' });
  } else {
    navLinks.unshift({ text: 'התחבר', url: '/login', variant: 'auth' });
  }

  return {
    navLinks,
    ctaLink: { text: 'הזמנה', url: '/order' },
  };
}

export function useSiteNavigation() {
  const store = useStore();

  const user = computed<IUserInfo | null>(
    () => store.getters['user/getUser'],
  );
  const hasActiveEvent = computed<boolean>(
    () => store.getters['event/hasActiveEvent'],
  );

  const navigation = computed<ISiteNavigation>(() =>
    buildSiteNavigation(user.value, hasActiveEvent.value),
  );

  const navLinks = computed(() => navigation.value.navLinks);
  const ctaLink = computed(() => navigation.value.ctaLink);

  return { navLinks, ctaLink, navigation, user, hasActiveEvent };
}
